import { useEffect, useState } from "react";
import { useAuth, API } from "@/App";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { motion } from "framer-motion";

const typeBadge = (type) => {
  const t = (type || "").toLowerCase();
  if (t === "review")  return <Badge style={{ backgroundColor: "#83E8BA20", color: "#689689" }} variant="secondary">Review</Badge>;
  if (t === "enquiry") return <Badge style={{ backgroundColor: "#68968915", color: "#689689" }} variant="secondary">Enquiry</Badge>;
  return <Badge style={{ backgroundColor: "#B2E6D4", color: "#4a7068" }} variant="secondary">Suggestion</Badge>;
};

const Inbox = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [messages, setMessages] = useState([]);

  const fetchMessages = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/contact/messages`, {
        credentials: "include",
      });
      if (!res.ok) {
        throw new Error("Failed");
      }
      const data = await res.json();
      setMessages(data.messages || []);
    } catch (e) {
      toast.error("Could not load inbox (admin access required)");
    } finally {
      setLoading(false);
    }
  };

  const deleteMessage = async (id) => {
    if (!window.confirm("Delete this message?")) return;
    try {
      const res = await fetch(`${API}/contact/messages/${id}`, {
        method: "DELETE",
        credentials: "include",
      });
      if (!res.ok) throw new Error("Failed");
      const data = await res.json();
      if (data.deleted) {
        toast.success("Deleted");
        fetchMessages();
      } else {
        toast.message("Nothing deleted");
      }
    } catch (e) {
      toast.error("Delete failed");
    }
  };

  useEffect(() => {
    fetchMessages();
  }, []);

  return (
    <div className="min-h-screen" style={{ backgroundColor: "#f5f2ec" }} data-testid="inbox-page">
      <main className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Card style={{ borderColor: "#B2E6D4", backgroundColor: "#ffffff" }}>
          <CardHeader>
            <CardTitle className="text-xl" style={{ color: "#504136" }}>Inbox</CardTitle>
            <CardDescription style={{ color: "#4a7068" }}>
              Contact form submissions (admin only). Logged in as: {user?.email}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex justify-end mb-4">
              <Button variant="outline" onClick={fetchMessages} style={{ borderColor: "#B2E6D4", color: "#4a7068" }}>
                Refresh
              </Button>
            </div>

            {loading ? (
              <p style={{ color: "#4a7068" }}>Loading...</p>
            ) : messages.length === 0 ? (
              <p style={{ color: "#4a7068" }}>No messages yet.</p>
            ) : (
              <div className="space-y-3">
                {messages.map((m, index) => (
                  <motion.div
                    key={m.id}
                    initial={{ opacity: 0, x: -16 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.35, delay: index * 0.05 }}
                    className="rounded-lg p-4"
                    style={{ border: "1px solid #B2E6D4", backgroundColor: "#edf8f4" }}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          {typeBadge(m.type)}
                          <span className="text-xs font-mono" style={{ color: "#4a7068" }}>{m.id}</span>
                        </div>
                        <p className="text-sm font-medium truncate" style={{ color: "#504136" }}>
                          {m.name || "Anonymous"}
                          <span style={{ color: "#4a7068", fontWeight: 400 }}>
                            {m.email ? ` • ${m.email}` : ""}
                          </span>
                        </p>
                        <p className="text-xs mt-1" style={{ color: "#4a7068" }}>{new Date(m.created_at).toLocaleString("en-IN")}</p>
                        <p className="text-sm mt-3 whitespace-pre-wrap" style={{ color: "#4a7068" }}>
                          {m.message}
                        </p>
                      </div>
                      <Button
                        variant="destructive"
                        onClick={() => deleteMessage(m.id)}
                      >
                        Delete
                      </Button>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default Inbox;
